/**
 * "Ir para o fim" vira o "Inserir campo".
 *
 * A seta da quina é o botão de inserir recolhido. Ao clicar, em dois tempos (pedido do
 * usuário: "desce tudo e depois vem para o botão", em linha reta):
 * 1. a lista rola até o fim, e a bolinha fica na quina com a seta chamando para baixo;
 * 2. rolagem terminada, a bolinha vai RETO até o lugar do botão, esticando de círculo para
 *    pílula; a seta some girando e o "+ Inserir campo" aparece.
 * Ao chegar, um pulso de pouso, e a cópia dá lugar ao botão de verdade (idêntico a ela).
 *
 * A rolagem é feita aqui, por `requestAnimationFrame`, e não com `scrollTo({ behavior:
 * 'smooth' })`: o navegador não diz quanto a rolagem dele dura, e o voo começa quando ela acaba.
 */

/** Tempo 1: a lista descendo. Total dos dois tempos: 0,5 s (pedido do usuário). */
const SCROLL_MS = 250;
/** Tempo 2: a bolinha indo até o botão. */
const TRAVEL_MS = 250;

/** Ease-in-out: sai devagar da quina, acelera no caminho e pousa suave. */
function ease(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function prefersReducedMotion(): boolean {
    return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Rola `scroller` até o fim levando a seta até o botão de inserir. Sem o botão na folha, ou
 * com movimento reduzido, só rola. Resolve quando tudo termina.
 */
export function flyArrowToInsert(
    scroller: HTMLElement,
    arrow: HTMLElement | null,
    fabHost: HTMLElement | null
): Promise<void> {
    const fab = fabHost?.querySelector<HTMLElement>('.insert-fab') ?? null;
    const startTop = scroller.scrollTop;
    const endTop = Math.max(0, scroller.scrollHeight - scroller.clientHeight);

    if (!arrow || !fabHost || !fab || prefersReducedMotion() || endTop <= startTop) {
        scroller.scrollTo({ top: endTop, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
        return Promise.resolve();
    }

    // Onde a seta está e onde o botão vai estar quando a rolagem acabar. O invólucro, e não o
    // botão: o botão fica deslocado até a animação de entrada dele rodar.
    const from = arrow.getBoundingClientRect();
    const host = fabHost.getBoundingClientRect();
    const width = fab.offsetWidth;
    const height = fab.offsetHeight;
    const to = {
        left: host.left + (host.width - width) / 2,
        top: host.top - (endTop - startTop),
        width,
        height,
    };

    const flight = buildFlight(arrow, fab, from);
    document.body.appendChild(flight.root);

    // A seta vira a cópia na hora (sem o fade de saída) e o botão real espera o pouso.
    arrow.style.visibility = 'hidden';
    fab.style.visibility = 'hidden';

    return new Promise(resolve => {
        const startedAt = performance.now();

        // A pessoa rolou no meio (roda, toque): a rolagem é dela. O voo termina na hora, sem
        // brigar com ela, e cada coisa volta ao seu lugar.
        let aborted = false;
        const abort = (): void => {
            aborted = true;
        };
        scroller.addEventListener('wheel', abort, { passive: true, once: true });
        scroller.addEventListener('touchmove', abort, { passive: true, once: true });
        const finish = (): void => {
            scroller.removeEventListener('wheel', abort);
            scroller.removeEventListener('touchmove', abort);
            fab.getAnimations({ subtree: true }).forEach(animation => animation.finish());
            fab.style.visibility = '';
            arrow.style.visibility = '';
            flight.root.remove();
            resolve();
        };

        const frame = (now: number): void => {
            // Saiu da tela no meio (trocou de seção, o botão sumiu): termina sem animar.
            if (aborted || !fabHost.isConnected || !scroller.isConnected) {
                finish();
                return;
            }

            const elapsed = now - startedAt;

            // Tempo 1: só a lista anda; a bolinha espera na quina.
            const scrollT = Math.min(1, elapsed / SCROLL_MS);
            scroller.scrollTop = startTop + (endTop - startTop) * ease(scrollT);

            // Tempo 2: a bolinha vai reto do centro da seta ao centro do botão. O destino é
            // relido a cada quadro depois da rolagem: se a folha mudou de altura no meio (foto
            // carregando, eco do servidor), a bolinha segue o botão em vez do lugar antigo.
            if (elapsed >= SCROLL_MS) {
                const live = fabHost.getBoundingClientRect();
                to.left = live.left + (live.width - width) / 2;
                to.top = live.top;
            }
            const t = Math.min(1, Math.max(0, (elapsed - SCROLL_MS) / TRAVEL_MS));
            const k = ease(t);

            // Estica depois de sair da quina: primeiro anda redonda, depois vira pílula.
            const stretch = ease(Math.max(0, (t - 0.25) / 0.75));
            const w = from.width + (to.width - from.width) * stretch;
            const h = from.height + (to.height - from.height) * stretch;

            const cx = from.left + from.width / 2 + (to.left + to.width / 2 - from.left - from.width / 2) * k;
            const cy = from.top + from.height / 2 + (to.top + to.height / 2 - from.top - from.height / 2) * k;

            Object.assign(flight.root.style, {
                left: `${cx - w / 2}px`,
                top: `${cy - h / 2}px`,
                width: `${w}px`,
                height: `${h}px`,
            });

            if (elapsed < SCROLL_MS + TRAVEL_MS) {
                requestAnimationFrame(frame);
                return;
            }

            // A animação de entrada do botão rodou escondida durante a rolagem: `finish`
            // termina ela, para ele aparecer inteiro no lugar da cópia.
            land(flight.root).then(finish);
        };

        requestAnimationFrame(frame);
    });
}

/**
 * A cópia que voa: pílula azul (a cor do botão lida do próprio botão) com a seta e o
 * conteúdo do botão sobrepostos. A seta sai girando no começo; o "+ Inserir campo" entra no
 * fim, quando a forma já é de pílula.
 */
function buildFlight(arrow: HTMLElement, fab: HTMLElement, from: DOMRect): { root: HTMLElement } {
    const fabStyle = getComputedStyle(fab);

    const root = document.createElement('div');
    root.setAttribute('aria-hidden', 'true');
    Object.assign(root.style, {
        position: 'fixed',
        left: `${from.left}px`,
        top: `${from.top}px`,
        width: `${from.width}px`,
        height: `${from.height}px`,
        zIndex: '1002',
        borderRadius: '999px',
        overflow: 'hidden',
        backgroundColor: fabStyle.backgroundColor,
        color: fabStyle.color,
        boxShadow: fabStyle.boxShadow,
        pointerEvents: 'none',
    } satisfies Partial<CSSStyleDeclaration>);

    const layer = (): HTMLElement => {
        const el = document.createElement('div');
        Object.assign(el.style, {
            position: 'absolute',
            inset: '0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        });
        return el;
    };

    // A seta, girando e sumindo.
    const arrowLayer = layer();
    const arrowIcon = arrow.querySelector('ra-icon')?.cloneNode(true);
    if (arrowIcon) arrowLayer.appendChild(arrowIcon);
    root.appendChild(arrowLayer);
    // Tempo 1: a seta chama para baixo enquanto a lista desce.
    arrowLayer.animate(
        [
            { transform: 'none' },
            { transform: 'translateY(0.1875rem)' },
            { transform: 'none' },
            { transform: 'translateY(0.1875rem)' },
            { transform: 'none' },
        ],
        { duration: SCROLL_MS, easing: 'ease-in-out' }
    );
    // Tempo 2: some girando assim que a bolinha sai da quina.
    arrowLayer.animate(
        [
            { opacity: 1, transform: 'none' },
            { opacity: 0, transform: 'rotate(90deg) scale(0.6)' },
        ],
        { duration: TRAVEL_MS * 0.4, delay: SCROLL_MS, easing: 'ease-in', fill: 'forwards' }
    );

    // O conteúdo do botão, entrando quando a forma já é de pílula. Mesmo espaçamento do
    // botão, para a troca no pouso não mexer um pixel.
    const content = layer();
    Object.assign(content.style, {
        gap: fabStyle.columnGap || '0.5rem',
        padding: fabStyle.padding,
        font: fabStyle.font,
        letterSpacing: fabStyle.letterSpacing,
        whiteSpace: 'nowrap',
    });
    fab.childNodes.forEach(node => content.appendChild(node.cloneNode(true)));
    root.appendChild(content);
    content.animate(
        [
            { opacity: 0, transform: 'translateY(0.375rem)', filter: 'blur(2px)' },
            { opacity: 1, transform: 'none', filter: 'blur(0)' },
        ],
        {
            duration: TRAVEL_MS * 0.45,
            delay: SCROLL_MS + TRAVEL_MS * 0.55,
            easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
            fill: 'both',
        }
    );

    return { root };
}

/** Pouso: um aperto de mola e um anel que se abre em volta. */
function land(root: HTMLElement): Promise<void> {
    const ring = document.createElement('div');
    Object.assign(ring.style, {
        position: 'fixed',
        left: root.style.left,
        top: root.style.top,
        width: root.style.width,
        height: root.style.height,
        borderRadius: '999px',
        pointerEvents: 'none',
        zIndex: '1001',
        boxShadow: `0 0 0 0 ${getComputedStyle(root).backgroundColor}`,
    });
    document.body.appendChild(ring);

    ring.animate(
        [
            { boxShadow: `0 0 0 0 color-mix(in srgb, ${root.style.backgroundColor} 45%, transparent)` },
            { boxShadow: `0 0 0 16px color-mix(in srgb, ${root.style.backgroundColor} 0%, transparent)` },
        ],
        { duration: 600, easing: 'ease-out' }
    ).finished.then(() => ring.remove(), () => ring.remove());

    return root
        .animate(
            [{ transform: 'scale(1)' }, { transform: 'scale(1.06)' }, { transform: 'scale(1)' }],
            { duration: 280, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }
        )
        .finished.then(
            () => undefined,
            () => undefined
        );
}
